// ============================================================
// store/stash-card-detail/context.tsx
//
// React Context that exposes a per-instance StashCardDetail store.
// Mirrors `store/stash-scoped/context.tsx` exactly so the patterns
// stay consistent.
// ============================================================

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useStore } from 'zustand';
import {
  createStashCardDetailStore,
  type StashCardDetailStoreApi,
} from './createStashCardDetailStore';
import type {
  StashCard,
  StashCardActivity,
  StashCardNote,
  Stasher,
  StashCategory,
  StashCardDetailStore,
} from './types';

const StashCardDetailStoreContext = createContext<StashCardDetailStoreApi | null>(null);

interface StashCardDetailStoreProviderProps {
  card: StashCard;
  stashers: Stasher[];
  categories: StashCategory[];
  currentUserStasherId: string;
  onLogActivity?: (activity: StashCardActivity) => void;
  onAddNote?: (note: StashCardNote) => void;
  children: ReactNode;
}

export function StashCardDetailStoreProvider({
  card,
  stashers,
  categories,
  currentUserStasherId,
  onLogActivity,
  onAddNote,
  children,
}: StashCardDetailStoreProviderProps): React.JSX.Element {
  // Memoize the store API across renders so subscriptions stay stable.
  const store = useMemo(
    () => createStashCardDetailStore({
      card,
      stashers,
      categories,
      currentUserStasherId,
      ...(onLogActivity ? { onLogActivity } : {}),
      ...(onAddNote ? { onAddNote } : {}),
    }),
    // Intentionally NOT including card/stashers/categories in deps — the store
    // owns its mutations after creation. Use `initialize` to re-seed when the
    // route changes (i.e. a different card id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <StashCardDetailStoreContext.Provider value={store}>
      {children}
    </StashCardDetailStoreContext.Provider>
  );
}

/**
 * Selector hook. Components subscribe to slices of detail state via:
 *   const card = useStashCardDetailStore(s => s.card);
 *   const log  = useStashCardDetailStore(s => s.logActivity);
 */
export function useStashCardDetailStore<T>(
  selector: (state: StashCardDetailStore) => T,
): T {
  const ctx = useContext(StashCardDetailStoreContext);
  if (!ctx) throw new Error('useStashCardDetailStore must be used inside <StashCardDetailStoreProvider>');
  return useStore(ctx, selector);
}
